import { ArrowUpIcon, CloseIcon, PlusIcon } from "@cynthai/icons";
import type { FocusEvent, ReactNode, Ref } from "react";
import { useContext, useState } from "react";
import {
  ButtonContext,
  Popover as RAPopover,
  Select as RASelect,
  SelectStateContext,
  SelectValue as RASelectValue,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";

import type { ButtonBaseProps } from "../Button";
import Button from "../Button";
import InputErrorMessage from "../InputErrorMessage";
import { m } from "../paraglide/messages";
import { type SelectItem, type SlotClassNames } from "../types";

const selectStyles = tv({
  slots: {
    base: "flex flex-col gap-0.5",
    label: "",
    popover:
      "flex w-(--trigger-width) min-w-[min(16rem,calc(100vw-1.5rem))] max-w-[calc(100vw-1.5rem)] flex-col overflow-hidden",
    listbox: "",
    option: "",
  },
});
import { NoResults, SearchableListShell } from "./SearchableListbox";
import { SelectChip } from "./SelectChip";
import { SelectLabel } from "./SelectLabel";
import { SelectListbox } from "./SelectListbox";
import type { SummaryFormatterFn } from "./utils";
import { defaultSummaryFormatter, SELECT_SEARCH_THRESHOLD } from "./utils";

export type SelectKey = string | number;

export type CreateOption = { label: string; onCreate: () => void };

// a key no caller item is expected to use
const CREATE_KEY = "\u0000select-create";

export type SelectValueDisplay = "summary" | "labels" | "chips" | "chips-below";

export type ItemValue<T extends readonly SelectItem<SelectKey>[]> =
  T[number]["value"];

export interface BaseSelectProps<
  T extends readonly SelectItem<SelectKey>[] = readonly SelectItem<SelectKey>[],
> {
  items: T;
  placeholder?: string;
  /** Defaults to search for lists with more than ten items. */
  isSearchable?: boolean;
  searchPlaceholder?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  /** presence renders the message and marks the field invalid */
  errorMessage?: string;
  buttonProps?: ButtonBaseProps;
  className?: string;
  classNames?: SlotClassNames<ReturnType<typeof selectStyles>>;
  name?: string;
  isOpen?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * with `isSearchable`, offers an extra row built from the typed query, e.g.
   * inviting a typed email. return null to hide it. pressing the row runs
   * `onCreate` and clears the query; it is never selected.
   */
  createOption?: (query: string) => CreateOption | null;
  /**
   * with `isSearchable`, reports the typed query (and "" once the popover
   * closes) so the caller can search on the server and pass back the matches
   */
  onSearchChange?: (query: string) => void;
  /** called when the end of the list scrolls into view, to load the next page */
  onLoadMore?: () => void;
  /** a page is on its way: shows the loader and holds off further onLoadMore */
  isLoadingMore?: boolean;
  onBlur?: (e: FocusEvent) => void;
  ref?: Ref<HTMLDivElement>;
}

type SelectLabelingProps =
  | { label: string; "aria-label"?: string }
  | { label?: undefined; "aria-label": string };

type SingleSelectionProps<V extends SelectKey> = {
  selectionMode?: "single";
  valueDisplay?: never;
  summaryFormatter?: never;
} & (
  | {
      /** renders a clear button in the trigger */
      isClearable: true;
      value?: V | null;
      defaultValue?: V | null;
      onChange?: (value: V | null) => void;
    }
  | {
      isClearable?: false;
      value?: V | null;
      defaultValue?: V | null;
      onChange?: (value: V) => void;
    }
);

type MultipleSelectionProps<V extends SelectKey> = {
  selectionMode: "multiple";
  isClearable?: never;
  value?: readonly V[];
  defaultValue?: readonly V[];
  onChange?: (value: V[]) => void;
  /** how the trigger renders more than one selected item */
  valueDisplay?: SelectValueDisplay;
  summaryFormatter?: SummaryFormatterFn;
};

export type SelectProps<
  T extends readonly SelectItem<SelectKey>[] = readonly SelectItem<SelectKey>[],
> = BaseSelectProps<T> &
  SelectLabelingProps &
  (SingleSelectionProps<ItemValue<T>> | MultipleSelectionProps<ItemValue<T>>);

function MultiTriggerValue<TItem extends SelectItem<SelectKey>>({
  placeholder,
  valueDisplay,
  summaryFormatter,
}: {
  readonly placeholder: string;
  readonly valueDisplay: SelectValueDisplay;
  readonly summaryFormatter: SummaryFormatterFn;
}) {
  return (
    <RASelectValue<TItem>
      className={`data-[placeholder=true]:text-text-secondary min-w-0 truncate`}
    >
      {({ selectedItems, isPlaceholder, state }) => {
        if (isPlaceholder) return placeholder;
        const selected = selectedItems.filter(
          (item): item is TItem => item != null,
        );
        if (selected.length === 1) return selected[0].label;
        if (valueDisplay === "labels") {
          return selected.map((item) => item.label).join(", ");
        }
        // wrapping chips regrow the trigger on every pick, dragging the
        // anchored popover with it: collapse to the summary while open
        if (valueDisplay === "chips" && !state.isOpen) {
          return (
            <span className="inline-flex flex-wrap gap-2">
              {selected.map((item) => (
                <span
                  key={item.value}
                  className="border-border/80 bg-secondary/70 text-text-primary rounded border px-2 py-1 text-xs leading-4"
                >
                  {item.label}
                </span>
              ))}
            </span>
          );
        }
        return summaryFormatter(selected.length);
      }}
    </RASelectValue>
  );
}

// the aux controls read and mutate RAC's SelectStateContext instead of the
// value/onChange props so they work for uncontrolled selects too

// RAC's Select provides trigger press props through ButtonContext to every
// nested Button; reset it so aux buttons don't toggle the popover
const detachedButtonContext = {};

function SelectBulkActions({
  items,
  canSelectAll,
}: {
  readonly items: readonly SelectItem<SelectKey>[];
  readonly canSelectAll: boolean;
}) {
  const state = useContext(SelectStateContext);
  const enabledValues = items
    .filter((item) => !item.disabled)
    .map((item) => item.value);
  if (!state || enabledValues.length === 0) return null;
  const selectedCount = Array.isArray(state.value) ? state.value.length : 0;
  return (
    <div className="border-border flex gap-1 border-b px-2 py-1.5">
      {canSelectAll && (
        <Button
          size="sm"
          color="secondary"
          className="text-xs"
          onPress={() => state.setValue(enabledValues)}
        >
          {m.select_all()}
        </Button>
      )}
      <Button
        size="sm"
        color="secondary"
        className="text-xs"
        isDisabled={selectedCount === 0}
        onPress={() => state.setValue([])}
      >
        {m.clear()}
      </Button>
    </div>
  );
}

function SelectClearButton() {
  const state = useContext(SelectStateContext);
  if (state?.value == null) return null;
  return (
    <ButtonContext.Provider value={detachedButtonContext}>
      <Button
        variant="ghost"
        className="absolute top-1/2 right-2 flex -translate-y-1/2"
        size="square-sm"
        icon={CloseIcon}
        aria-label="clear selection"
        onClick={(e) => {
          e.stopPropagation();
          state.setValue(null);
        }}
      />
    </ButtonContext.Provider>
  );
}

function SelectPopoverBody<TItem extends SelectItem<SelectKey>>({
  isSearchable,
  isLoading,
  accessibleLabel,
  searchPlaceholder,
  query,
  onQueryChange,
  bulkActions,
  listbox,
}: {
  readonly isSearchable: boolean;
  readonly isLoading: boolean;
  readonly accessibleLabel?: string;
  readonly searchPlaceholder?: string;
  readonly query: string;
  readonly onQueryChange: (query: string) => void;
  readonly bulkActions: ReactNode;
  readonly listbox: ReactNode;
}) {
  if (!isSearchable) {
    if (!bulkActions) return <>{listbox}</>;
    return (
      <div className="border-border bg-secondary flex min-h-0 flex-col overflow-hidden rounded-md border">
        {bulkActions}
        {listbox}
      </div>
    );
  }

  return (
    <div className="border-border bg-secondary flex min-h-0 flex-col overflow-hidden rounded-md border">
      <SearchableListShell<TItem>
        accessibleLabel={accessibleLabel}
        searchPlaceholder={searchPlaceholder}
        isLoading={isLoading}
        autoFocus
        inputValue={query}
        onInputChange={onQueryChange}
      >
        {bulkActions}
        {listbox}
      </SearchableListShell>
    </div>
  );
}

function SelectChipsBelow({ isDisabled }: { readonly isDisabled: boolean }) {
  const state = useContext(SelectStateContext);
  const selected = state?.selectedItems ?? [];
  if (!state || selected.length === 0) return null;
  const currentValues = Array.isArray(state.value) ? state.value : [];
  return (
    <ButtonContext.Provider value={detachedButtonContext}>
      <div className="mt-2 flex flex-wrap gap-2">
        {selected.map((node) => {
          const item = node.value as SelectItem<SelectKey> | null;
          if (!item) return null;
          return (
            <SelectChip
              key={node.key}
              label={item.label}
              icon={item.icon}
              badge={item.badge}
              isDisabled={isDisabled}
              onRemove={() =>
                state.setValue(currentValues.filter((v) => v !== item.value))
              }
            />
          );
        })}
      </div>
    </ButtonContext.Provider>
  );
}

function useCreateRow<TItem extends SelectItem<SelectKey>>(
  items: readonly TItem[],
  query: string,
  createOption: ((query: string) => CreateOption | null) | undefined,
) {
  const created = query.trim() ? createOption?.(query) : null;
  const listItems: readonly TItem[] = created
    ? [
        ...items,
        {
          value: CREATE_KEY,
          // the filter matches on the label, and the query always contains itself
          label: query,
          renderContent: () => (
            <span className="flex items-center gap-2">
              <PlusIcon aria-hidden width={16} height={16} />
              {created.label}
            </span>
          ),
        } as TItem,
      ]
    : items;
  return {
    listItems,
    isCreateSelection: (next: unknown) =>
      next === CREATE_KEY || (Array.isArray(next) && next.includes(CREATE_KEY)),
    runCreate: () => created?.onCreate(),
  };
}

export default function Select<
  const T extends readonly SelectItem<SelectKey>[],
>(allProps: SelectProps<T>) {
  const {
    items,
    buttonProps,
    label,
    "aria-label": ariaLabel,
    placeholder,
    className,
    classNames,
    isSearchable = items.length > SELECT_SEARCH_THRESHOLD,
    searchPlaceholder,
    isLoading = false,
    isDisabled = false,
    isRequired = false,
    errorMessage,
    name,
    isOpen,
    defaultOpen,
    onOpenChange,
    createOption,
    onSearchChange,
    onLoadMore,
    isLoadingMore = false,
    onBlur,
    ref,
    selectionMode = "single",
    value,
    defaultValue,
    onChange,
    isClearable = false,
    valueDisplay = "summary",
    summaryFormatter = defaultSummaryFormatter,
  } = allProps;

  const isMultiple = selectionMode === "multiple";
  const [query, setQuery] = useState("");
  const changeQuery = (next: string) => {
    setQuery(next);
    onSearchChange?.(next);
  };
  // uncontrolled selects keep their value here rather than in RAC, so the
  // create row's key can never land in the selection
  const isControlled = value !== undefined;
  const [uncontrolledValue, setUncontrolledValue] = useState<
    SelectKey | readonly SelectKey[] | null
  >(defaultValue ?? (isMultiple ? [] : null));
  const { listItems, isCreateSelection, runCreate } = useCreateRow(
    items,
    isSearchable ? query : "",
    createOption,
  );
  const isInvalid = !!errorMessage;
  const accessibleLabel = label ?? ariaLabel;
  const displayPlaceholder = placeholder ?? "Choose...";

  const listboxClassName = isSearchable
    ? "max-h-64"
    : isMultiple
      ? undefined
      : "border-border border";

  const listbox = (
    <SelectListbox<ItemValue<T>, T[number]>
      items={listItems}
      aria-label={accessibleLabel}
      selectionMode={selectionMode}
      className={twMerge(listboxClassName, classNames?.listbox)}
      classNames={{ option: classNames?.option }}
      renderEmptyState={isSearchable ? () => <NoResults /> : undefined}
      onLoadMore={onLoadMore}
      isLoadingMore={isLoadingMore}
    />
  );

  return (
    <div
      className={selectStyles().base({
        className: twMerge(className, classNames?.base),
      })}
    >
      <SelectLabel
        label={label}
        isRequired={isRequired}
        className={classNames?.label}
      />
      <div className="relative flex-1">
        <RASelect<T[number], "single" | "multiple">
          ref={ref}
          selectionMode={selectionMode}
          value={isControlled ? value : uncontrolledValue}
          onChange={(next) => {
            // RAC only runs a row's action while nothing is selected, so the
            // create row goes through selection and is intercepted here
            if (isCreateSelection(next)) {
              runCreate();
              changeQuery("");
              return;
            }
            if (!isControlled) setUncontrolledValue(next);
            // the public union guarantees the payload matches the mode
            (onChange as ((v: typeof next) => void) | undefined)?.(next);
          }}
          className="group"
          placeholder={displayPlaceholder}
          isInvalid={isInvalid}
          isDisabled={isDisabled || isLoading}
          allowsEmptyCollection={isSearchable}
          name={name}
          isOpen={isOpen}
          defaultOpen={defaultOpen}
          onOpenChange={(open) => {
            if (!open) changeQuery("");
            onOpenChange?.(open);
          }}
          onBlur={onBlur}
          aria-label={accessibleLabel}
        >
          <Button
            color="secondary"
            size="lg"
            isLoading={isLoading}
            {...buttonProps}
            className={twMerge(
              `aria-expanded:border-border relative w-full border border-transparent focus:outline-0`,
              buttonProps?.className,
              isClearable && `not-has-data-[placeholder=true]:pr-12`,
              isInvalid && "border-danger! aria-expanded:border-danger!",
            )}
          >
            {isMultiple ? (
              <MultiTriggerValue<T[number]>
                placeholder={displayPlaceholder}
                valueDisplay={valueDisplay}
                summaryFormatter={summaryFormatter}
              />
            ) : (
              <RASelectValue
                className={`data-[placeholder=true]:text-text-secondary min-w-0 truncate *:flex-row **:[[slot=description]]:before:content-['('] **:[[slot=description]]:after:content-[')']`}
              />
            )}
            <ArrowUpIcon
              aria-hidden
              width={20}
              height={20}
              className={`ml-auto shrink-0 rotate-180 transition-transform duration-100 group-data-open:rotate-0`}
            />
          </Button>

          <RAPopover
            className={selectStyles().popover({
              className: classNames?.popover,
            })}
            placement="bottom right"
          >
            <SelectPopoverBody<T[number]>
              isSearchable={isSearchable}
              isLoading={isLoading}
              accessibleLabel={accessibleLabel}
              searchPlaceholder={searchPlaceholder}
              query={query}
              onQueryChange={changeQuery}
              bulkActions={
                isMultiple ? (
                  // items searched or paged on the server are one page, not all
                  <SelectBulkActions
                    items={items}
                    canSelectAll={!onSearchChange && !onLoadMore}
                  />
                ) : null
              }
              listbox={listbox}
            />
          </RAPopover>

          {!isMultiple && isClearable && <SelectClearButton />}
          {valueDisplay === "chips-below" && (
            <SelectChipsBelow isDisabled={isDisabled} />
          )}
        </RASelect>
      </div>

      {isInvalid && <InputErrorMessage>{errorMessage}</InputErrorMessage>}
    </div>
  );
}
